import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useEventSearch } from '../hooks/useEventSearch';
import { useInfiniteScrollTrigger } from '../hooks/useInfiniteScrollTrigger';
import EventGrid from '../components/events/EventGrid';
import EventGridSkeleton from '../components/events/EventGridSkeleton';
import Header from '../components/layout/Header';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';

const EventsPage = () => {
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [mobileQuery, setMobileQuery] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlFilters = useMemo(
    () => ({
      category: searchParams.get('category') || '',
      city: searchParams.get('city') || '',
      priceRange: '',
      sortBy: searchParams.get('sortBy') || 'date',
      sortOrder: searchParams.get('sortOrder') || 'asc'
    }),
    [searchParams]
  );

  const {
    events,
    loading,
    loadingMore,
    error,
    pagination,
    searchQuery,
    filters,
    search,
    updateFilters,
    loadMore,
    clearAll,
    hasActiveFilters
  } = useEventSearch(urlFilters);

  useEffect(() => {
    const q = searchParams.get('search');
    if (q) {
      search(q);
      setMobileQuery(q);
    } else {
      search('');
      setMobileQuery('');
    }
    updateFilters(urlFilters);
  }, [searchParams, search, updateFilters, urlFilters]);

  const handleClearFilters = () => {
    clearAll();
    setMobileQuery('');
    navigate('/events');
  };

  const handleMobileSearch = (e) => {
    e.preventDefault();
    const q = mobileQuery.trim();
    if (q) {
      navigate(`/events?search=${encodeURIComponent(q)}`);
    } else {
      navigate('/events');
    }
  };

  const sentinelRef = useInfiniteScrollTrigger({
    onLoadMore: loadMore,
    hasMore: pagination.hasNextPage,
    loading: loading || loadingMore
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollPosition > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const gridClassName = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4';

  return (
    <SiteLayout className="bg-gray-50">
      <Header />

      <SiteMain>
        <PageBreadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Events' }]} />

        <div className="container mx-auto px-4 py-8">
          {/* Mobile-only search - flat, no shadows */}
          <form
            onSubmit={handleMobileSearch}
            className="md:hidden mb-5"
            role="search"
          >
            <label htmlFor="events-mobile-search" className="sr-only">
              Search events
            </label>
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
              <input
                id="events-mobile-search"
                type="search"
                value={mobileQuery}
                onChange={(e) => setMobileQuery(e.target.value)}
                placeholder="Search events, venues, cities…"
                className="w-full pl-9 pr-20 py-2.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 outline-none focus:border-orange-400"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 rounded-md"
              >
                Search
              </button>
            </div>
          </form>

          {loading ? (
            <>
              <div className="mb-6 animate-pulse">
                <div className="h-7 bg-gray-200 rounded-md w-48 max-w-full" />
              </div>
              <EventGridSkeleton className={gridClassName} />
            </>
          ) : (
            <>
              {!error && (
                <div className="mb-7">
                  <h1 className="text-xl md:text-2xl font-medium text-gray-800 tracking-tight">
                    {searchQuery ? `Search results for "${searchQuery}"` : 'Discover vibes'}
                  </h1>
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                    <p className="text-red-600 font-medium">{error}</p>
                    <p className="text-red-500 text-sm mt-2">Make sure the backend server is running on port 5000</p>
                  </div>
                </div>
              )}

              {!error && events.length === 0 && (
                <div className="text-center py-12">
                  <div className="bg-white border border-gray-100 rounded-md p-8 max-w-md mx-auto">
                    <div className="text-5xl mb-4">🎪</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {searchQuery || filters.category || hasActiveFilters ? 'No events found' : 'No events yet'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {searchQuery || filters.category
                        ? 'Try browsing all events or pick another category.'
                        : 'Check back soon for amazing events!'}
                    </p>
                    {(searchQuery || filters.category) && (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="text-sm font-medium text-orange-600 border border-orange-200 hover:bg-orange-50 px-4 py-2 rounded-md transition-colors"
                      >
                        View all events
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!error && events.length > 0 && (
                <EventGrid
                  events={events}
                  sentinelRef={sentinelRef}
                  hasMore={pagination.hasNextPage}
                  loadingMore={loadingMore}
                  className={gridClassName}
                />
              )}
            </>
          )}

          {showScrollToTop && (
            <button
              type="button"
              onClick={scrollToTop}
              className="fixed bottom-24 right-6 bg-orange-500 text-white p-3 rounded-md shadow-lg hover:bg-orange-600 transition-all duration-300 z-40"
              title="Back to top"
              aria-label="Back to top"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          )}
        </div>
      </SiteMain>
    </SiteLayout>
  );
};

export default EventsPage;
